import { InjectableService } from "@/shared/services/InjectableService";
import { SchemaService } from "@/shared/services/SchemaService";
import { ISchema } from "@/shared/models/ISchema";
import { DeploymentPrefix } from "@/shared/models/DeploymentPrefix";
import { ICustomSchemaStore } from "@/shared/models/ICustomSchemaStore";
import { DataCleaningSchemaService } from "@/components/DataCleaning/services/DataCleaningSchemaService";
import { AnalysisConfigSchemaService } from "@/components/AnalysisConfig/services/AnalysisConfigSchemaService";
import { CalculationSchemaService } from "@/components/CustomCalculations/services/CalculationSchemaService";
import { PlottingSchemaService } from "@/components/Plotting/services/PlottingSchemaService";
import { TablesSchemaService } from "@/components/Tables/services/TableSchemaService";
import { CustomReportSchemaService } from "@/components/CustomReports/services/CustomReportSchemaService";
import { cdnSchemaStoreTypeMap } from "../models/CdnSchemaStoreTypeMap";
import { CdnSchemaType, CdnSchemaTypeToSchemaMap } from "../models/CdnSchemaType";
import { AdvancedAnalysisSchemaService } from "@/components/AdvancedAnalysis/services/AdvancedAnalysisSchemaService";

export class CdnSchemaFactory extends InjectableService {

    static readonly injectableKey = 'CdnSchemaFactory';

    get injectableKey() {
        return (this.constructor as typeof CdnSchemaFactory).injectableKey;
    }

    private readonly _schemaServiceByKey: Record<string, SchemaService<any>>;

    constructor(
        private readonly _dataCleaningSchemaService: DataCleaningSchemaService,
        private readonly _analysisConfigSchemaService: AnalysisConfigSchemaService,
        private readonly _calculationSchemaService: CalculationSchemaService,
        private readonly _plottingSchemaService: PlottingSchemaService,
        private readonly _tablesSchemaService: TablesSchemaService,
        private readonly _customReportSchemaService: CustomReportSchemaService,
        private readonly _advancedAnalysisSchemaService: AdvancedAnalysisSchemaService
    ) {
        super();
        this._schemaServiceByKey = {
            [DataCleaningSchemaService.injectableKey]: this._dataCleaningSchemaService,
            [AnalysisConfigSchemaService.injectableKey]: this._analysisConfigSchemaService,
            [CalculationSchemaService.injectableKey]: this._calculationSchemaService,
            [PlottingSchemaService.injectableKey]: this._plottingSchemaService,
            [TablesSchemaService.injectableKey]: this._tablesSchemaService,
            [CustomReportSchemaService.injectableKey]: this._customReportSchemaService,
            [AdvancedAnalysisSchemaService.injectableKey]: this._advancedAnalysisSchemaService
        };
    }

    public async loadAvailableSchemasForCdnSchemaType<TType extends CdnSchemaType>(
        cdnSchemaType: TType,
        customSchemaStore: ICustomSchemaStore): Promise<Array<CdnSchemaTypeToSchemaMap[TType]>> {

        if (cdnSchemaType == null) throw Error("cdnSchemaType is required.");
        if (customSchemaStore == null) throw Error("customSchemaStore is required.");

        const { injectableKey, storeLocationProp, storeSchemasProp } = cdnSchemaStoreTypeMap[cdnSchemaType];

        const schemaService = this._schemaServiceByKey[injectableKey];
        const schemaLocation = customSchemaStore[storeLocationProp] as DeploymentPrefix | undefined;
        const storedSchemas = customSchemaStore[storeSchemasProp] as Array<ISchema> | undefined;

        const cdnResult =
            schemaLocation === DeploymentPrefix.Custom && storedSchemas?.length
                ? storedSchemas
                : await schemaService.getSchemas(schemaLocation);

        const cdnSchemas = Array.isArray(cdnResult) ? cdnResult : [];

        return [...cdnSchemas, ...schemaService.getInternalSchemas()];
    }
}